import { Injectable } from '@angular/core';
import { AdvancedMessagingService as ApiAdvancedMessagingService } from '../api/services/advanced-messaging.service';
import { MessagingGroupModel } from '../api/models/messaging-group-model';
import { MessageModel } from '../api/models/message-model';
import { BehaviorSubject, lastValueFrom, Subject, Subscription } from 'rxjs';
import { EventsService } from '../events/events.service';
import { LocalNotificationsService } from '../core/services/local-notifications.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { NotificationModel } from '../api/models/notification-model';
import { ApiConfiguration } from '../api/api-configuration';
import { BillingCycleLimitModel, GridSortModel, MediaModel, SendProMessageModel, SmsPhoneNumberModel } from '../api/models';

@Injectable()
export class ProMessagingService {

  getNumbersInProgress = true;
  showOnlyMyNumbers = true;

  filteredMessagingNumbers: SmsPhoneNumberModel[] = [];
  messagingNumbers: SmsPhoneNumberModel[] = [];
  selectedMessagingNumber: SmsPhoneNumberModel = { phoneNumber: '' };
  totalGroups = 0;
  messagingGroups = new BehaviorSubject<MessagingGroupModel[]>([]);
  onNewMessageSubject = new BehaviorSubject<MessageModel | undefined>(undefined);
  onDeletedMessageSubject = new BehaviorSubject<MessageModel | undefined>(undefined);
  onWebSocketConnectSubject = new Subject<void>();
  onNewNotificationsSubject = new BehaviorSubject<NotificationModel[]>([]);
  notificationsSubscription: Subscription;
  isComposing = false;

  private hasAssignUserAccess = false;

  constructor(private eventService: EventsService,
              private notificationsService: LocalNotificationsService,
              private httpClient: HttpClient,
              private apiConfig: ApiConfiguration,
              private apiMessagingService: ApiAdvancedMessagingService,
  ) {

    this.eventService.subscribeEvents('sms-deleted', (event, data: MessageModel) => {
      this.onDeletedMessageSubject.next(data);
    });

    this.eventService.subscribeEvents('sms-in', (event, data: MessageModel) => {
      this.onNewMessageSubject.next(data);
    });

    this.eventService.subscribeEvents('sms-out', (event, data: MessageModel) => {
      this.onNewMessageSubject.next(data);
    });

    this.eventService.subscribeEvents('connect', (event, data: MessageModel) => {
      this.onWebSocketConnectSubject.next();
    });

    this.notificationsSubscription = this.notificationsService.latestNotifications.subscribe((data) => {
      this.onNewNotificationsSubject.next(data);
      this.updatePhoneNumbersBadges(data);
    });
  }

  getMessagingNumber(): string {
    return this.selectedMessagingNumber?.phoneNumber || '';
  }

  hasMessagingNumber() {
    return this.messagingNumbers.length > 0;
  }

  async getMessagingLimits(): Promise<BillingCycleLimitModel> {
    const response = await lastValueFrom(this.apiMessagingService.proMessagingGetMessagingLimits());
    return response.data;
  }

  public async queryPhoneNumberThreads(options: {
    phoneNumber: string,
    skip?: number,
    limit?: number,
    searchText?: string,
    sort?: Array<GridSortModel>,
    threadKey?: string,
    forceUpdate: boolean
  }): Promise<MessagingGroupModel[]> {
    console.log('update queryPhoneNumberGroups:', options.phoneNumber);

    if (!options.phoneNumber) {
      this.messagingGroups.next([]);
      return [];
    }

    const response = await lastValueFrom(this.apiMessagingService.proMessagingGetMessagingGroupsV2({
      phoneNumber: options.phoneNumber,
      body: {
        searchText: options.searchText,
        sort: options.sort,
        skip: options.skip || 0,
        limit: options.limit || 100,
        phoneNumber: options.phoneNumber,
        user: undefined,
      },
    }));
    const allGroups = response.data;
    this.totalGroups = response.total;

    await this.updateGroupsBadges(allGroups, this.notificationsService.latestNotifications.getValue());

    return allGroups;
  }

  public async updateGroupsBadges(groups: MessagingGroupModel[], notifications: NotificationModel[]) {
    console.log('updateGroupsBadges: ' + notifications.length);
    let hasNotifications = false;
    for (const group of groups) {
      const notification = notifications
        .find(x => x.eventType === 'pro-sms' && x.tnTo === this.getMessagingNumber() && x.thread === group.threadKey);

      if (notification) {
        hasNotifications = true;
        group.unread = notification.badge;
      } else {
        group.unread = 0;
      }
    }

    if (hasNotifications) {
      const messagingGroups = this.messagingGroups.getValue();
      this.messagingGroups.next(messagingGroups);
    }
  }

  public updatePhoneNumbersBadges(notifications: NotificationModel[]) {
    console.log('updatePhoneNumbersBadges: ' + notifications.length);
    for (const number of this.filteredMessagingNumbers) {
      const phoneNotifications = notifications.filter(x => x.eventType === 'pro-sms' && x.tnTo === number.phoneNumber);
      let totalBadges = 0;
      for (const notification of phoneNotifications) {
        totalBadges += notification.badge;
      }
      number.badge = totalBadges;
    }
  }

  public async sendMessage(request: SendProMessageModel, file?: File): Promise<MessageModel> {
    if (file) {
      const mediaModel = await this.uploadMedia(file, request.wabaDid);
      if (mediaModel) {

        if (request.template) {
          const headerObj = (request.template.components || []).find(x => x.type === 'header');

          // check if contains header image;
          if (headerObj) {
            const headerObjParams = headerObj?.parameters || [];
            const imageParam = headerObjParams.find(x => x.type === 'image');
            if (imageParam) {
              imageParam.image!.id = mediaModel.mediaId;
            }

            const documentParam = headerObjParams.find(x => x.type === 'document');
            if (documentParam) {
              documentParam.document!.id = mediaModel.mediaId;
            }

            const videoParam = headerObjParams.find(x => x.type === 'video');
            if (videoParam) {
              videoParam.video!.id = mediaModel.mediaId;
            }
          }
        }

        request.media = [mediaModel];
      }
    }

    const response = await lastValueFrom(this.apiMessagingService.proMessagingSendMessage({ body: request }));

    if (response) {
      console.log(response.messageId);
      return response;
    }

    return undefined;
  }

  public async uploadMedia(file: File, wabaDid: string): Promise<MediaModel> {

    const options: any = {
      method: 'post',
      url: this.apiConfig.rootUrl + `/api/advanced-messaging/upload-media`,
      params: new HttpParams(),
      headers: new HttpHeaders(),
    };
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    if (wabaDid) {
      formData.append('wabaDid', wabaDid);
    }

    return await lastValueFrom(this.httpClient.post<MediaModel>(options.url, formData, {}));
  }

  public async deleteMessageBodyById(messageId: string): Promise<MessageModel> {
    return await lastValueFrom(this.apiMessagingService.proMessagingDeleteMessageBody({ messageId: messageId }));
  }

  public async deleteFullConversation(threadKey: string, broadcastId: string, block: boolean): Promise<boolean> {
    if (!!broadcastId) {
      return await lastValueFrom(this.apiMessagingService.proMessagingDeleteFullConversationByBroadcastId({ broadcastId: broadcastId }));
    }
    if (!!threadKey) {
      return await lastValueFrom(this.apiMessagingService.proMessagingDeleteFullConversationByGroup({ groupKey: threadKey, block: block }));
    }

    return false;
  }

  public async pinConversation(threadKey: string, pin: boolean): Promise<boolean> {
    if (!!threadKey) {
      return await lastValueFrom(this.apiMessagingService.proMessagingPinConversationByGroup({ groupKey: threadKey, pin: pin }));
    }

    return false;
  }

  public async markUnreadMessages(group?: MessagingGroupModel): Promise<void> {

    if (!this.getMessagingNumber() || !group) {
      return;
    }

    await this.notificationsService.markUnreadNotifications(0, this.getMessagingNumber() + '-' + group.threadKey);

    const messagingGroups = this.messagingGroups.getValue();
    this.messagingGroups.next(messagingGroups);
  }

  public async markUnreadMessagesByThreadKey(threadKey: string): Promise<void> {

    if (!this.getMessagingNumber()) {
      return;
    }

    await this.notificationsService.markUnreadNotifications(0, this.getMessagingNumber() + '-' + threadKey);

    const messagingGroups = this.messagingGroups.getValue();
    this.messagingGroups.next(messagingGroups);
  }

  public async markMessageAsUnreadByThreadKey(threadKey: string): Promise<void> {

    if (!this.getMessagingNumber()) {
      return;
    }

    await this.notificationsService.markMessageAsUnread(1, this.getMessagingNumber() + '-' + threadKey);

    const messagingGroups = this.messagingGroups.getValue();
    this.messagingGroups.next(messagingGroups);
  }

  public async messagingPhoneNumbers(tn?: string, force?: boolean): Promise<SmsPhoneNumberModel[]> {
    console.log('messagingPhoneNumbers:', tn, force);
    this.getNumbersInProgress = true;
    if (!this.messagingNumbers || this.messagingNumbers.length === 0 || force) {
      this.messagingNumbers = await lastValueFrom(this.apiMessagingService.proMessagingGetMyMessagingPhoneNumbers());
    }
    this.getNumbersInProgress = false;

    this.filterMessagingNumbers(force || false, tn);

    return this.messagingNumbers;
  }

  public async isInBlackList(senderNumber: string, recipientNumber: string): Promise<boolean> {
    const response = await lastValueFrom(this.apiMessagingService.proMessagingIsInBlackList({
      senderNumber: senderNumber,
      recipientNumber: recipientNumber,
    }));

    return response.data;
  }

  public async removeFromBlackList(senderNumber: string, recipientNumber: string): Promise<boolean> {
    const response = await lastValueFrom(this.apiMessagingService.proMessagingRemoveFromBlackList({
      senderNumber: senderNumber,
      recipientNumber: recipientNumber,
    }));

    return response.data;
  }

  public getContactsAndTagsObservable(searchText?: string) {
    return this.apiMessagingService.proMessagingLookupContactsAndTags({ q: searchText || '' });
  }

  filterMessagingNumbers(force: boolean, tn?: string) {
    if (this.showOnlyMyNumbers) {
      this.filteredMessagingNumbers = this.messagingNumbers.filter(x => x.allowSend);
      if (this.filteredMessagingNumbers.length == 0) {
        if (this.hasAssignUserAccess) {
          this.filteredMessagingNumbers.push({
            phoneNumber: undefined,
            userName: '+ Add New Number',
            badge: undefined,
            allowSend: false,
            campaignId: 'FAKE',
          });
        }
      }
    } else {
      this.filteredMessagingNumbers = [...this.messagingNumbers];
    }

    this.updatePhoneNumbersBadges(this.notificationsService.latestNotifications.getValue());

    if (force || (!this.getMessagingNumber() && this.filteredMessagingNumbers.length > 0)) {
      this.selectedMessagingNumber = this.filteredMessagingNumbers.find(x => x.phoneNumber === tn);
      if (!this.selectedMessagingNumber) {
        this.selectedMessagingNumber = this.filteredMessagingNumbers.find(x => x.badge > 0) || this.filteredMessagingNumbers[0];
      }
    }
  }

  setHasAssignUserAccess(hasAccess: boolean): void {
    this.hasAssignUserAccess = hasAccess;
    this.filterMessagingNumbers(false);
  }

  showAllNumbers(showOnlyMy: boolean): void {
    console.log('showAllNumbers', showOnlyMy);
    this.showOnlyMyNumbers = showOnlyMy;

    const force = this.showOnlyMyNumbers && !this.selectedMessagingNumber?.allowSend;

    this.filterMessagingNumbers(force, this.selectedMessagingNumber?.phoneNumber);
  }
}
